// frontend/src/pages/HdPurchase.jsx
import React, { useState, useEffect } from "react";
import { Button, SelectInput } from "../ui/UIComponents";

export default function HdPurchase() {
  // Mode selection & List management
  const [poList, setPoList] = useState([]);
  const [editingPoId, setEditingPoId] = useState(null); // null means "New PO" mode
  const [loading, setLoading] = useState(false);

  // Header State
  const [seller, setSeller] = useState("");
  const [sellerOptions, setSellerOptions] = useState([]); // Holds opt_type_id=46 dictionary data
  const [poDate, setPoDate] = useState(new Date().toISOString().split("T")[0]);
  const [poReference, setPoReference] = useState("");
  const [exportDocReq, setExportDocReq] = useState([]); // Array of strings (e.g., ['CO', 'CQ'])
  const [partialShipment, setPartialShipment] = useState("No");
  const [terms, setTerms] = useState("EXW");

  // Details Line Items State
  const [lines, setLines] = useState([]);

  // Fetch POs and Seller options for dropdown switching/editing context
  useEffect(() => {
    fetchSavedPOs();
    fetchSellerOptions();
  }, []);

  const fetchSellerOptions = async () => {
    try {
      const res = await fetch("/api/data?scope=origin");
      if (res.ok) {
        const json = await res.json();
        setSellerOptions(json.rows || []);
      }
    } catch (err) {
      console.error("Error loading seller dictionary options:", err);
    }
  };

  const fetchSavedPOs = async () => {
    try {
      const res = await fetch("/api/data?scope=purchase_order&action=list");
      if (res.ok) {
        const json = await res.json();
        setPoList(json.items || []);
      }
    } catch (err) {
      console.error("Error loading POs:", err);
    }
  };

  // Handle loading an existing PO for editing
  const handleLoadPO = async (poId) => {
    if (!poId) {
      resetForm();
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/data?scope=purchase_order&action=get&po_id=${poId}`);
      if (res.ok) {
        const json = await res.json();
        const data = json.data;
        if (data) {
          setEditingPoId(data.po_id);
          setSeller(data.seller || "");
          setPoDate(data.po_date ? data.po_date.split("T")[0] : "");
          setPoReference(data.po_reference || "");
          setExportDocReq(data.export_doc_req || []);
          setPartialShipment(data.partial_shipment || "No");
          setTerms(data.terms || "EXW");
          
          // CRITICAL FIX: Explicitly cast database numeric strings to native numbers 
          // to completely eradicate JavaScript string concatenation math anomalies.
          const formattedLines = (data.lines || []).map(line => ({
            ...line,
            line_seq: Number(line.line_seq),
            qty: Number(line.qty) || 0,
            unit_price: Number(line.unit_price) || 0,
            line_total: Number(line.line_total) || 0,
            up_inv: Number(line.up_inv) || 0
          }));
          setLines(formattedLines);
        }
      }
    } catch (err) {
      alert("Failed to load PO details.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form completely back to "Create New" state
  const resetForm = () => {
    setEditingPoId(null);
    setSeller("");
    setPoDate(new Date().toISOString().split("T")[0]);
    setPoReference("");
    setExportDocReq([]);
    setPartialShipment("No");
    setTerms("EXW");
    setLines([]);
  };

  // Triggers master data retrieval ONLY when user finishes interaction with the field
  const executeStockLookup = async (index) => {
    const targetLine = lines[index];
    const code = targetLine.item_code || "";

    if (!code.trim()) {
      const updatedLines = [...lines];
      updatedLines[index].description = "";
      updatedLines[index].origin = "";
      updatedLines[index].unit_price = 0;
      updatedLines[index].line_total = 0;
      setLines(updatedLines);
      return;
    }

    try {
      // API lookup to hd_stock via unified scope multiplexer
      const res = await fetch(`/api/data?scope=purchase_order&action=stock_lookup&item_code=${encodeURIComponent(code.trim())}`);
      if (res.ok) {
        const json = await res.json();
        const stockData = json.item;
        const updatedLines = [...lines];
        
        if (stockData) {
          updatedLines[index].description = stockData.description || "";
          updatedLines[index].origin = stockData.origin || ""; // NEW ADDITION: Map origin from ledger table
          // FIX: Automatically map and bind the exact Unit of Measure (UOM) from master inventory
          updatedLines[index].unit = stockData.uom || "Pcs";
          updatedLines[index].unit_price = Number(stockData.unit_price) || 0;
          updatedLines[index].line_total = updatedLines[index].qty * (Number(stockData.unit_price) || 0);
        } else {
          updatedLines[index].description = "⚠️ Code not found in master ledger";
          updatedLines[index].origin = "";
          updatedLines[index].unit_price = 0;
          updatedLines[index].line_total = 0;
        }
        setLines(updatedLines);
      }
    } catch (err) {
      console.error("Stock master item lookup error:", err);
    }
  };

  // Modify local row configurations safely
  const handleLineFieldChange = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index][field] = value;

    if (field === "qty" || field === "unit_price") {
      const q = field === "qty" ? Number(value) : Number(updatedLines[index].qty);
      const p = field === "unit_price" ? Number(value) : Number(updatedLines[index].unit_price);
      updatedLines[index].line_total = q * p;
    }
    setLines(updatedLines);
  };

  const addLineItem = () => {
    const nextSeq = lines.length + 1;
    setLines([
      ...lines,
      {
        line_seq: nextSeq,
        item_code: "",
        origin: "",
        description: "",
        unit: "Pcs",
        qty: 0.000,
        unit_price: 0.0000,
        line_total: 0.0000,
        notes: "",
        up_inv: 0.0000 // Saved behind scenes
      }
    ]);
  };

  const removeLineItem = (index) => {
    const remaining = lines.filter((_, i) => i !== index);
    // Re-sequence remaining items sequentially
    const reSequenced = remaining.map((line, i) => ({
      ...line,
      line_seq: i + 1
    }));
    setLines(reSequenced);
  };

  const handleDocCheckboxChange = (docType) => {
    if (exportDocReq.includes(docType)) {
      setExportDocReq(exportDocReq.filter(item => item !== docType));
    } else {
      setExportDocReq([...exportDocReq, docType]);
    }
  };

  // Compiles the entire component PO matrix into a structured text report sheet
  const handleDownloadTxt = () => {
    if (lines.length === 0) {
      alert("No line items available to export.");
      return;
    }

    const padL = (str, len) => String(str || "").padStart(len);
    const padR = (str, len) => String(str || "").padEnd(len);
    const selectedSellerName = sellerOptions.find(o => String(o.opt_id) === String(seller))?.opt_name || "Unknown Vendor";
    
    const linesBuffer = [];
    const now = new Date();
    const dateStamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // 1. Document Header Information Block
    linesBuffer.push("========================================================================================");
    linesBuffer.push("               RAW MATERIALS & COMPONENT PURCHASE ORDER MANIFEST (XUẤT ĐƠN HÀNG VẬT TƯ) ");
    linesBuffer.push("========================================================================================");
    linesBuffer.push(`PO Status     : ${editingPoId ? `EDITING PROFILE (ID: ${editingPoId})` : "NEW FRESH SHEET"}`);
    linesBuffer.push(`Export Date   : ${dateStamp} ${now.toTimeString().split(" ")[0]}`);
    linesBuffer.push(`Seller/Vendor : ${selectedSellerName} (Master Index ID: ${seller || "N/A"})`);
    linesBuffer.push(`PO Date Field : ${poDate}`);
    linesBuffer.push(`PO Reference  : ${poReference || "No Reference Logged"}`);
    linesBuffer.push(`Terms Profile : ${terms} | Partial Shipment Allowed: ${partialShipment}`);
    linesBuffer.push(`Export Doc Req: ${exportDocReq.length > 0 ? exportDocReq.join(", ") : "None Specified"}`);
    linesBuffer.push("========================================================================================");
    linesBuffer.push("");

    // 2. Tabular Column Matrix Definitions with Rigid Character Channels
    linesBuffer.push(
      padR("STT", 4) + " | " +
      padR("Mã Vật Tư(Code)", 16) + " | " +
      padR("Xuất Xứ", 10) + " | " +
      padR("Mô Tả Chi Tiết (Description)", 45) + " | " +
      padR("Đơn Vị(UoM)", 12) + " | " +
      padL("SL (Qty)", 12) + " | " +
      padL("Giá EXW", 12) + " | " +
      padL("SO UP", 12) + " | " +
      padL("Thành Tiền", 14)
    );
    linesBuffer.push("--------------------------------------------------------------------------------------------------------------------------------------------------------------");

    // 3. Matrix Mapping Grid Loop
    lines.forEach((line) => {
      linesBuffer.push(
        padR(String(line.line_seq), 4) + " | " +
        padR(String(line.item_code || "").substring(0, 16), 16) + " | " +
        padR(String(line.origin || "—").substring(0, 10), 10) + " | " +
        padR(String(line.description || "—").substring(0, 45), 45) + " | " +
        padR(String(line.unit || "Pcs"), 12) + " | " +
        padL(Number(line.qty || 0).toFixed(3), 12) + " | " +
        padL(Number(line.unit_price || 0).toFixed(4), 12) + " | " +
        padL(Number(line.up_inv || 0).toFixed(4), 12) + " | " +
        padL(Number(line.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }), 14)
      );

      if (line.notes) {
        linesBuffer.push(`    ↳ Ghi chú/Notes: ${line.notes}`);
      }
    });

    linesBuffer.push("--------------------------------------------------------------------------------------------------------------------------------------------------------------");
    linesBuffer.push(padL(`PO GRAND TOTAL VALUE: $${calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD`, 154));
    linesBuffer.push("========================================================================================");

    const fullTextBody = linesBuffer.join("\n");
    const sanitizedRef = (poReference || "NEW_PO").trim().replace(/\s+/g, "_");
    const compiledFileName = `PO_COMPONENTS_${sanitizedRef}_${dateStamp}.txt`;

    // Dispatch parameters down into the active global window helper execution loop
    if (typeof window !== "undefined" && typeof window.dl_prod_ord_config_txt === "function") {
      window.dl_prod_ord_config_txt({ filename: compiledFileName, text: fullTextBody });
    } else {
      // Fallback baseline download block structure
      const blob = new Blob([fullTextBody], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = compiledFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Form Submissions Strategy (Both INSERT and UPDATE matching editingPoId status)
  const handleSavePO = async (e) => {
    e.preventDefault();
    if (!seller.trim()) {
      alert("Seller is a required header metric.");
      return;
    }

    const payload = {
      po_id: editingPoId,
      seller,
      po_date: poDate,
      po_reference: poReference,
      export_doc_req: exportDocReq,
      partial_shipment: partialShipment,
      terms,
      lines
    };

    try {
      // Unified entry routes both new inserts and edits to a single data query switch
      const res = await fetch("/api/data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "purchase_order",
          action: "save",
          ...payload
        })
      });

      if (res.ok) {
        alert(editingPoId ? "Purchase Order updated successfully!" : "Purchase Order created successfully!");
        resetForm();
        fetchSavedPOs();
      } else {
        alert("Transaction processing failed on backend server workflows.");
      }
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  // Grand total aggregation with explicit floating number fallback configuration
  const calculatedGrandTotal = lines.reduce((acc, current) => acc + (Number(current.line_total) || 0), 0);

  // Cross-reference current master selection data for formal printing headers
  const printingSellerName = sellerOptions.find(o => String(o.opt_id) === String(seller))?.opt_name || "_______________________________";

  // Re-format Order Date layout string from yyyy-mm-dd to dd-mm-yyyy for print view
  const printFormattedDate = poDate && poDate.includes("-") 
    ? poDate.split("-").reverse().join("-") 
    : poDate;

  return (
    <div className="app-page" style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* SCOPED INVOICE WEB-PRINT OVERLAY CORE RULES */}
      <style>{`
        @media screen {
          .formal-print-document { display: none !important; }
        }
        @media print {
          /* 1. Vaporize all layout components that are not the print-mirror canvas template */
          .app-page > div:not(.formal-print-document),
          .app-page > form {
            display: none !important;
          }
          /* 2. Unveil and expand the formal corporate document canvas */
          .formal-print-document {
            display: block !important;
            width: 100% !important;
            color: #000 !important;
            font-family: "Segoe UI", 'Arial', sans-serif !important;
            font-size: 13px !important;
            line-height: 1.5 !important;
          }
          .po-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          .po-table th {
            background: #f0f0f0 !important;
            border: 1px solid #111 !important;
            padding: 7px 5px !important;
            font-weight: bold !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .po-table td {
            border: 1px solid #111 !important;
            padding: 6px 5px !important;
            vertical-align: top;
          }
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2>Purchase Department: Order Module</h2>
        <div>
          <label style={{ marginRight: "8px", fontWeight: "600" }}>Edit Existing Order: </label>
          <select 
            className="text-input" 
            style={{ width: "220px", display: "inline-block" }}
            value={editingPoId || ""}
            onChange={(e) => handleLoadPO(e.target.value)}
          >
            <option value="">-- Create New Purchase Order --</option>
            {poList.map((po) => (
              <option key={po.po_id} value={po.po_id}>
                ID: {po.po_id} | {po.seller} ({po.po_reference || "No Ref"})
              </option>
            ))}
          </select>
          {editingPoId && (
            <Button onClick={resetForm} style={{ marginLeft: "10px", background: "#f0f0f0" }}>
              Clear / New
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>Loading transactional records...</div>
      ) : (
        <form onSubmit={handleSavePO}>
          {/* SECTION 1: PO HEADER */}
          <div className="page-section" style={{ marginBottom: "24px" }}>
            <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
              {editingPoId ? `PO Header Profile — Editing ID: ${editingPoId}` : "PO Header Profile"}
            </h3>
            
            <div className="page-grid">
              <div>
                <label className="label">1. Seller *</label>
                <select
                  className="text-input"
                  value={seller}
                  onChange={(e) => setSeller(e.target.value)}
                  required
                >
                  <option value="">-- Select Master Vendor --</option>
                  {sellerOptions.map((opt) => (
                    <option key={opt.opt_id} value={opt.opt_id}>
                      {opt.opt_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">2. PO Date</label>
                <input 
                  type="date" 
                  className="text-input" 
                  value={poDate} 
                  onChange={(e) => setPoDate(e.target.value)} 
                />
              </div>

              <div>
                <label className="label">3. PO Reference</label>
                <input 
                  type="text" 
                  className="text-input" 
                  value={poReference} 
                  onChange={(e) => setPoReference(e.target.value)} 
                  placeholder="User editable internal tracking index"
                />
              </div>

              <div>
                <label className="label">5. Partial Shipment</label>
                <SelectInput 
                  value={partialShipment} 
                  onChange={(e) => setPartialShipment(e.target.value)} 
                  choices={["No", "Yes"]} 
                />
              </div>

              <div>
                <label className="label">6. Terms</label>
                <SelectInput 
                  value={terms} 
                  onChange={(e) => setTerms(e.target.value)} 
                  choices={["EXW", "FOB", "CIF"]} 
                />
              </div>

              <div>
                <label className="label">4. Export Doc Requirements</label>
                <div style={{ display: "flex", gap: "16px", marginTop: "10px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input 
                      type="checkbox" 
                      checked={exportDocReq.includes("CO")} 
                      onChange={() => handleDocCheckboxChange("CO")}
                    /> CO
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input 
                      type="checkbox" 
                      checked={exportDocReq.includes("CQ")} 
                      onChange={() => handleDocCheckboxChange("CQ")}
                    /> CQ
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: PO DETAILS */}
          <div className="page-section" style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0 }}>PO Line Items Detailed Matrix</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button 
                  type="button" 
                  onClick={() => window.open("https://web-advisor-dev.vercel.app/purchase/config/retail-product", "_blank")} 
                  style={{ background: "#fff", color: "#d32f2f", borderColor: "#d32f2f", fontWeight: "600" }}
                >
                  Check Item Code
                </Button>
                <Button type="button" onClick={addLineItem} style={{ background: "#eef9ff", color: "#0066cc", fontWeight: "600" }}>
                  + Append Item Row
                </Button>
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "1100px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                  <th style={{ padding: "8px", width: "45px" }}>Seq</th>
                  <th style={{ padding: "8px", width: "160px" }}>Item Code</th>
                  <th style={{ padding: "8px", width: "100px" }}>Origin</th>
                  <th style={{ padding: "8px" }}>Description (Auto Lookup)</th>
                  <th style={{ padding: "8px", width: "95px" }}>Unit</th>
                  <th style={{ padding: "8px", width: "100px" }}>Qty</th>
                  <th style={{ padding: "8px", width: "110px" }}>Unit Price</th>
                  <th style={{ padding: "8px", width: "110px" }}>SO UP</th>
                  <th style={{ padding: "8px", width: "110px" }}>Line Total</th>
                  <th style={{ padding: "8px", width: "160px" }}>Notes</th>
                  <th style={{ padding: "8px", width: "50px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                      No sequence items added. Click "+ Append Item Row" to build the transaction profile.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "6px", fontWeight: "bold" }}>{line.line_seq}</td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="text" 
                          className="text-input" 
                          style={{ padding: "4px" }}
                          value={line.item_code} 
                          onChange={(e) => handleLineFieldChange(index, "item_code", e.target.value)}
                          onBlur={() => executeStockLookup(index)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); executeStockLookup(index); } }}
                          placeholder="Code lookup..."
                        />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="text" 
                          className="text-input" 
                          style={{ padding: "4px" }}
                          value={line.origin || ""} 
                          onChange={(e) => handleLineFieldChange(index, "origin", e.target.value)}
                        />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="text" 
                          className="text-input" 
                          style={{ padding: "4px", background: "#fcfcfc", color: "#222", width: "100%", minWidth: "280px" }}
                          value={line.description} 
                          disabled 
                          title={line.description}
                          placeholder="Automatic match from master ledger"
                        />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <select 
                          className="text-input" 
                          style={{ padding: "4px" }}
                          value={line.unit} 
                          onChange={(e) => handleLineFieldChange(index, "unit", e.target.value)}
                        >
                          <option value="Pcs">Pcs</option>
                          <option value="Set">Set</option>
                          <option value="M">M</option>
                          {/* Dynamic fallback option injector: handles unexpected UOM strings from master ledger securely */}
                          {line.unit && !["Pcs", "Set", "M"].includes(line.unit) && (
                            <option value={line.unit}>{line.unit}</option>
                          )}
                        </select>
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="number" 
                          step="0.0001" 
                          className="text-input" 
                          style={{ padding: "4px", textAlign: "right" }}
                          value={line.qty} 
                          onChange={(e) => handleLineFieldChange(index, "qty", e.target.value)}
                        />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="number" 
                          step="0.0001" 
                          className="text-input" 
                          style={{ padding: "4px", textAlign: "right" }}
                          value={line.unit_price} 
                          onChange={(e) => handleLineFieldChange(index, "unit_price", e.target.value)}
                        />
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="number" 
                          step="0.0001" 
                          className="text-input" 
                          style={{ padding: "4px", textAlign: "right", background: "#fffdf0", borderColor: "#ffe5b0" }}
                          value={line.up_inv} 
                          onChange={(e) => handleLineFieldChange(index, "up_inv", e.target.value)}
                          placeholder="0.0000"
                        />
                      </td>
                      <td style={{ padding: "6px", textAlign: "right", fontWeight: "600" }}>
                        {Number(line.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "6px" }}>
                        <input 
                          type="text" 
                          className="text-input" 
                          style={{ padding: "4px" }}
                          value={line.notes} 
                          onChange={(e) => handleLineFieldChange(index, "notes", e.target.value)}
                        />
                      </td>
                      <td style={{ padding: "6px", textAlign: "center" }}>
                        <button 
                          type="button" 
                          onClick={() => removeLineItem(index)}
                          style={{ background: "none", border: "none", color: "#cc0000", cursor: "pointer", fontSize: "16px" }}
                          title="Drop line row"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {lines.length > 0 && (
              <div style={{ float: "right", marginTop: "16px", fontSize: "16px", fontWeight: "700" }}>
                PO Aggregate Sum Total: {calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            )}
          </div>

          {/* CONTROL PROCESSOR ACTIONS */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            {lines.length > 0 && (
              <div style={{ marginRight: "auto", display: "flex", gap: "8px" }}>
                <Button 
                  type="button" 
                  onClick={handleDownloadTxt} 
                  style={{ background: "#fcfcfc", color: "#2e7d32", borderColor: "#2e7d32", fontWeight: "700" }}
                >
                  💾 Export Text Manifest (.TXT)
                </Button>
                <Button 
                  type="button" 
                  onClick={() => window.print()} 
                  style={{ background: "#f5f5f5", color: "#455a64", borderColor: "#b0bec5", fontWeight: "700" }}
                >
                  🖨️ Print / Save PDF
                </Button>
              </div>
            )}
            <Button type="button" onClick={resetForm} style={{ background: "#fff", borderColor: "#ccc" }}>
              Reset Changes
            </Button>
            <Button type="submit" style={{ background: "#0088cc", color: "#fff", fontWeight: "700" }}>
              {editingPoId ? "Commit Updates (Save PO)" : "Authorize New Purchase Order"}
            </Button>
          </div>
        </form>
      )}

      {/* ============================================================================ */}
      {/* HARD-MIRRORED FORMAL CORPORATE DOCUMENT PRINTING ARCHITECTURE               */}
      {/* ============================================================================ */}
      <div className="formal-print-document">
        {/* Document Header Panel */}
        <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "3px solid #111", paddingBottom: "12px", marginBottom: "20px" }}>
          <div>
            {/* Added Limited entity title extension and removed internal subheader lines */}
            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#111" }}>TUISS VIETNAM LIMITED</h2>
          </div>
          <div style={{ textAlign: "right" }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "800", letterSpacing: "1px" }}>PURCHASE ORDER</h1>
            <div style={{ fontSize: "12px", fontWeight: "600", marginTop: "4px" }}>
              PO ID: <span style={{ color: "#d32f2f" }}>{editingPoId || "Draft Fresh"}</span>
            </div>
          </div>
        </div>

        {/* Transaction Meta Parameters Summary Table */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "20px" }}>
          <div style={{ border: "1px solid #aaa", padding: "10px", borderRadius: "4px" }}>
            <div style={{ fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#666", marginBottom: "5px" }}>Vendor Entity (NCC)</div>
            <div style={{ fontSize: "14px", fontWeight: "700" }}>{printingSellerName}</div>
            {/* Purged loose master catalog ID string references from document proof sheet */}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", border: "1px solid #aaa", padding: "10px", borderRadius: "4px", fontSize: "12px" }}>
            {/* Displaying day-month-year structured output date string */}
            <div><b>Order Date:</b> {printFormattedDate}</div>
            <div><b>Terms:</b> {terms}</div>
            <div><b>Reference:</b> {poReference || "None Logged"}</div>
            <div><b>Partial Shipment:</b> {partialShipment}</div>
            <div style={{ gridColumn: "1 / span 2" }}><b>Certificates Required:</b> {exportDocReq.length > 0 ? exportDocReq.join(", ") : "None"}</div>
          </div>
        </div>

        {/* Clean Vector Table Grid Matrix */}
        <table className="po-table">
          <thead>
            <tr>
              <th style={{ width: "40px" }}>SN</th>
              <th style={{ width: "130px" }}>Item Code / Mã Vật Tư</th>
              <th style={{ width: "70px" }}>Origin</th>
              <th>Item Desc / Mô Tả Chi Tiết</th>
              <th style={{ width: "55px" }}>UoM / Đơn Vị</th>
              <th style={{ width: "80px", textAlign: "right" }}>Qty / SL</th>
              <th style={{ width: "95px", textAlign: "right" }}>EXW</th>
              {/* Purged internal database SO UP column from vendor facing profile sheet */}
              <th style={{ width: "110px", textAlign: "right" }}>Sub total / Thành Tiền</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.line_seq}>
                <td style={{ textAlign: "center", fontWeight: "600" }}>{line.line_seq}</td>
                <td>{line.item_code}</td>
                <td style={{ textAlign: "center" }}>{line.origin || "—"}</td>
                <td>
                  <div style={{ fontWeight: "600" }}>{line.description}</div>
                  {line.notes && <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "3px", color: "#444" }}>↳ Notes: {line.notes}</div>}
                </td>
                <td style={{ textAlign: "center" }}>{line.unit}</td>
                <td style={{ textAlign: "right" }}>{Number(line.qty || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td style={{ textAlign: "right" }}>${Number(line.unit_price || 0).toFixed(4)}</td>
                {/* Purged internal database SO UP data cell node mapping parameter loop */}
                <td style={{ textAlign: "right", fontWeight: "700" }}>${Number(line.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Accumulation Summary Total Blocks */}
        <div style={{ marginTop: "15px", textAlign: "right", fontSize: "15px", fontWeight: "800", paddingRight: "5px" }}>
          PO GRAND TOTAL VALUE: ${calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD
        </div>

        {/* Corporate Signatures Execution Authorization Panel */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginTop: "60px", textAlign: "center", pageBreakInside: "avoid" }}>
          <div>
            <div style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase" }}>- Seller -</div>
            <div style={{ marginTop: "50px", borderTop: "1px dashed #777", display: "inline-block", width: "180px" }}></div>
          </div>
          <div>
            <div style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase" }}>- Buyer -</div>
            <div style={{ marginTop: "50px", borderTop: "1px dashed #777", display: "inline-block", width: "180px" }}></div>
          </div>
        </div>
      </div>
      {/* ============================================================================ */}
    </div>
  );
}